import {Alert, Button, FormControl, openDialog} from 'platform/components';
import {UseDocumentPreviewReturnType} from 'platform/document-preview';
import {Heading, HStack, Icon, VStack, Text, Show, Hide} from 'platform/foundation';

import {UseFormReturn, useWatch} from 'react-hook-form';

import i18n from '@dms/i18n';

import {DOT_CHARACTER, pushGtmEvent, useDebouncedValue} from 'shared/web';

import {DelayEnum} from '../constants/DelayEnum';
import {useInvalidItemsCount} from '../hooks/useInvalidItemsCount';
import {DeliveryNoteDocumentPreviewFormDefinition} from '../types/DeliveryNoteDocumentPreviewFormDefinition';
import {BulkAssignManufacturer} from './BulkAssignManufacturer';

interface ListOfItemsHeaderProps {
  isConfirmed: boolean;
  formApi: UseFormReturn<DeliveryNoteDocumentPreviewFormDefinition>;
  control: FormControl<DeliveryNoteDocumentPreviewFormDefinition>;
  pageNumber: number;
  hasMoreThanOnePage: boolean;
  documentPreview: UseDocumentPreviewReturnType;
}

export function ListOfItemsHeader(props: ListOfItemsHeaderProps) {
  const listOfItems = useWatch({control: props.control, name: 'listOfItems'});

  const debouncedListOfItems = useDebouncedValue(listOfItems, DelayEnum.HEADER);

  const {invalidItemsCount, invalidItemsCountPerPage, invalidManufacturersCount} =
    useInvalidItemsCount({
      isConfirmed: props.isConfirmed,
      pageNumber: props.pageNumber,
      listOfItems: debouncedListOfItems,
      documentPreview: props.documentPreview,
    });

  const hasSelectedItems = debouncedListOfItems.some((item) => item.isSelected);

  const hasInvalidItems = invalidItemsCount > 0;

  const hasInvalidItemsOnCurrentPage = invalidItemsCountPerPage > 0;

  const hasInvalidManufacturers = invalidManufacturersCount > 0;

  const handleBulkAssignManufacturer = (manufacturerId: string) => {
    debouncedListOfItems.forEach((item, index) => {
      if (!item.isSelected || item.manufacturer) {
        return;
      }
      props.formApi.setValue(`listOfItems.${index}.manufacturer`, manufacturerId);
    });
  };

  return (
    <VStack spacing={4}>
      <HStack justify="space-between">
        <Heading size={4}>{i18n.t('entity.warehouse.labels.listOfItems')}</Heading>

        <Hide when={!props.isConfirmed}>
          <HStack spacing={2} align="center">
            <Show when={hasInvalidManufacturers}>
              <Button
                size="small"
                variant="link"
                title={i18n.t('entity.documentExtraction.actions.bulkAssignManufacturer', {
                  count: invalidManufacturersCount,
                })}
                onClick={() => {
                  pushGtmEvent({
                    event: 'button_click',
                    event_id: 'ai_tool_dn_open_bulk_assign_manufacturer_dialog',
                  });

                  openDialog(
                    <BulkAssignManufacturer
                      dialogId={BULK_ASSIGN_MANUFACTURER_DIALOG_ID}
                      onAssign={(manufacturerId) => handleBulkAssignManufacturer(manufacturerId)}
                    />,
                    {
                      id: BULK_ASSIGN_MANUFACTURER_DIALOG_ID,
                      title: i18n.t('entity.documentExtraction.actions.bulkAssignManufacturer', {
                        count: invalidManufacturersCount,
                      }),
                      size: 'small',
                    }
                  );
                }}
              />
            </Show>

            <Hide when={!hasSelectedItems}>
              <HStack spacing={1}>
                <HStack spacing={1}>
                  <Icon
                    value={hasInvalidItems ? 'alert/error' : 'action/check_circle'}
                    color={hasInvalidItems ? 'severity.danger' : 'severity.success'}
                    size={4}
                  />
                  <Text size="xSmall">{i18n.t('entity.documentExtraction.labels.allPages')}</Text>
                  <Text size="xSmall" color="tertiary">
                    {DOT_CHARACTER}
                  </Text>
                </HStack>
                <Text size="xSmall">
                  {i18n.t('entity.documentExtraction.labels.issues', {count: invalidItemsCount})}
                </Text>
              </HStack>
            </Hide>
          </HStack>
        </Hide>
      </HStack>

      <Show when={!props.isConfirmed}>
        <Alert
          type="inline"
          variant="info"
          title={i18n.t('entity.documentExtraction.labels.itemsAreNotAvailable')}
          message={i18n.t('entity.documentExtraction.labels.itemsAreNotAvailableMessage')}
        />
      </Show>

      <Hide when={!props.isConfirmed || !hasSelectedItems}>
        <Alert
          type="inline"
          variant={hasInvalidItemsOnCurrentPage ? 'error' : 'success'}
          title={
            hasInvalidItemsOnCurrentPage
              ? i18n.t('entity.documentExtraction.labels.invalidPageAlert', {
                  pageNumber: props.pageNumber,
                  count: invalidItemsCountPerPage,
                })
              : i18n.t('entity.documentExtraction.labels.successPageAlert', {
                  pageNumber: props.pageNumber,
                })
          }
          message={
            props.hasMoreThanOnePage && hasInvalidItems && !hasInvalidItemsOnCurrentPage
              ? i18n.t('entity.documentExtraction.labels.successPageAlertMessage')
              : undefined
          }
        />
      </Hide>

      <Show when={!hasSelectedItems}>
        <Alert
          type="inline"
          variant="warning"
          title={i18n.t('entity.documentExtraction.labels.noItemsSelectedAlert')}
          message={i18n.t('entity.documentExtraction.labels.noItemsSelectedAlertMessage')}
        />
      </Show>
    </VStack>
  );
}

const BULK_ASSIGN_MANUFACTURER_DIALOG_ID = 'bulk-assign-manufacturer-dialog';
